import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSeo } from "../lib/seo";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useSeo({ title: "Payment status", description: "Confirming your upgrade.", path: "/billing/success", noindex: true });

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          refresh();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        attempts.current += 1;
        if (attempts.current >= 6) { setStatus("pending"); return; }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        attempts.current += 1;
        if (attempts.current >= 6) { setStatus("error"); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refresh]);

  return (
    <div className="container flex min-h-[60vh] flex-col items-center justify-center gap-5 text-center">
      {status === "checking" && (<><Loader2 className="h-12 w-12 animate-spin text-primary" /><h1 className="font-display text-3xl">Confirming your payment…</h1></>)}
      {status === "paid" && (<>
        <CheckCircle2 className="h-14 w-14 text-success" />
        <h1 className="font-display text-4xl">You're upgraded! 🎉</h1>
        <p className="text-muted-foreground">Your plan is active. Unlimited progress tracking and the full AI Coach plan are unlocked.</p>
        <Link to="/dashboard" data-testid="success-dashboard-btn" className="rounded-md bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground">Go to dashboard</Link>
      </>)}
      {(status === "pending") && (<>
        <Loader2 className="h-12 w-12 text-primary" />
        <h1 className="font-display text-3xl">Payment processing</h1>
        <p className="text-muted-foreground">This can take a moment. Check your dashboard shortly.</p>
        <Link to="/dashboard" className="rounded-md bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground">Go to dashboard</Link>
      </>)}
      {(status === "error" || status === "expired") && (<>
        <XCircle className="h-14 w-14 text-destructive" />
        <h1 className="font-display text-3xl">Something went wrong</h1>
        <p className="text-muted-foreground">Your payment wasn't completed. You can try again anytime.</p>
        <Link to="/pricing" className="rounded-md bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground">Back to pricing</Link>
      </>)}
    </div>
  );
}
